// apps/client/src/ui/plotPick.ts — the plot picker's rules (founding and invites). On a landscape phone a plot is drawn 6–15 px wide, far
// below a finger, so a plot is picked for you (the free plot nearest the town square) and a tap anywhere on the map picks the pickable
// plot whose centre is nearest the tap. Distances are in map tiles; exact ties go to the lower plot index, so a pick never depends on
// the order plots are listed in.
import type { Plot, Rect, Vec2 } from '@whiskerton/shared';

export const plotCentre = (p: Plot): Vec2 => ({ x: p.rect.x + p.rect.w / 2, y: p.rect.y + p.rect.h / 2 });

/** The plots a player may pick: not taken, and on the free list when there is one (an invite's preview). */
export function pickablePlots(plots: readonly Plot[], taken?: readonly { plot: number }[], free?: readonly number[]): number[] {
  return plots.filter((p) => !taken?.some((x) => x.plot === p.index) && (!free || free.includes(p.index))).map((p) => p.index);
}

/** The pickable plot whose centre is nearest `at` (map tiles), or null when none can be picked. */
export function nearestPlot(plots: readonly Plot[], pickable: readonly number[], at: Vec2): number | null {
  let best: number | null = null, bestD = Infinity;
  for (const p of plots) {
    if (!pickable.includes(p.index)) continue;
    const c = plotCentre(p), d = Math.hypot(c.x - at.x, c.y - at.y);
    if (d < bestD || (d === bestD && best !== null && p.index < best)) { best = p.index; bestD = d; }
  }
  return best;
}

/** The plot picked for you before you choose: the pickable plot nearest the middle of the town square. */
export function defaultPlot(map: { plots: readonly Plot[]; sites: { plaza: Rect } }, pickable: readonly number[]): number | null {
  const { plaza } = map.sites;
  return nearestPlot(map.plots, pickable, { x: plaza.x + plaza.w / 2, y: plaza.y + plaza.h / 2 });
}

/** A screen point → map tiles, given the map's on-screen box. Null off the map, or before the map has a size. */
export function toMapPoint(clientX: number, clientY: number, box: { left: number; top: number; width: number; height: number },
  map: { w: number; h: number }): Vec2 | null {
  if (!(box.width > 0 && box.height > 0)) return null;
  const fx = (clientX - box.left) / box.width, fy = (clientY - box.top) / box.height;
  if (fx < 0 || fx > 1 || fy < 0 || fy > 1) return null;
  return { x: fx * map.w, y: fy * map.h };
}

/** The shortest distance between two plot centres (map tiles): how big a plot marker may be drawn before neighbours cover it. */
export function minPlotSpacing(plots: readonly Plot[]): number {
  let min = Infinity;
  for (let i = 0; i < plots.length; i++) {
    for (let j = i + 1; j < plots.length; j++) {
      const a = plotCentre(plots[i]!), b = plotCentre(plots[j]!);
      min = Math.min(min, Math.hypot(a.x - b.x, a.y - b.y));
    }
  }
  return min;
}
